import { AccessTime, Circle, Navigation } from '@mui/icons-material'
import {
    AppBar,
    Avatar,
    Box,
    Paper,
    Skeleton,
    Stack,
    Toolbar,
    Typography,
} from '@mui/material'
import { Grid, UniversalGoogleMapV2 } from '../../../../../UniversalComponents'
import { BoatModelChart } from '../../../charts/BoatModelChart'

import { BoastStatusChart } from '../../../charts/BoatStatusChart'

import { useCallback, useEffect, useReducer, useRef, useState } from 'react'
import { useSocket } from '../../../../../../context/SocketContext'
import { useAuth } from '../../../../../../context/AuthenticationContext'
import React from 'react'

import { useGlobalState } from '../../../../../../context/GlobalStateContext'

interface FleetGpsObject {
    lat: number
    lng: number
    name: string
    status: 'online' | 'offline' | 'idle' | undefined
}

const FleetHomeView: React.FC = () => {
    const { auth } = useAuth()
    const { globalState } = useGlobalState()
    const { oupUser } = globalState
    const { activeManufacturer } = oupUser
    const { user } = auth
    const { apiSocket } = useSocket()

    const defaultState = {
        fleetGps: [],
        fleetTable: [],
        fleetTotal: 0,
        fleetOnline: 0,
        fleetOffline: 0,
        fleetAwake: 0,
        fleetRunning: 0,
        fleetShutDown: 0,
        utcTime: '',
        boatModelData: [],
        boatStatusData: [],
        loading: true,
    }

    const reducer = (state, newState) => ({
        ...state,
        ...newState,
    })
    const [state, setState] = useReducer(reducer, defaultState)

    const {
        loading,
        fleetGps,
        fleetTotal,
        fleetOnline,
        fleetOffline,
        fleetAwake,
        fleetRunning,
        fleetShutDown,
        utcTime,
        boatModelData,
        boatStatusData,
    } = state

    const processFullFleet = useCallback(
        (rawData: any[]) => {
            if (!activeManufacturer?._id) {
                setState({ loading: false })
                return
            }

            const gpsPoints: FleetGpsObject[] = []
            let online = 0
            let offline = 0
            let running = 0
            let awake = 0
            let shutdown = 0
            let filteredCount = 0
            const modelCount: Record<string, number> = {}

            rawData.forEach((item) => {
                const boat = item.boatData
                const tel = item.telemetryData
                if (boat.manufacturer?._id !== activeManufacturer._id) return

                filteredCount++

                const statusRaw = (tel.status?.latest_value ?? 'OFFLINE')
                    .trim()
                    .toUpperCase()
                const isOnline = ['ONLINE', 'RUNNING', 'AWAKE'].includes(
                    statusRaw
                )

                switch (statusRaw) {
                    case 'ONLINE':
                        online++
                        break
                    case 'RUNNING':
                        running++

                        break
                    case 'AWAKE':
                        awake++

                        break
                    case 'SHUTDOWN':
                        shutdown++
                        break
                    default:
                        offline++
                        break
                }

                const model = boat.boatModel?.model ?? 'Unknown'
                modelCount[model] = (modelCount[model] || 0) + 1

                const lat = parseFloat(
                    tel.GPS_Latitude?.latest_value ||
                        tel.GPS_LATITUDE?.latest_value ||
                        ''
                )
                const lng = parseFloat(
                    tel.GPS_Longitude?.latest_value ||
                        tel.GPS_LONGITUDE?.latest_value ||
                        ''
                )

                if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
                    gpsPoints.push({
                        lat,
                        lng,
                        name:
                            boat.nickname ||
                            boat.fullHin ||
                            `Boat ${boat._id.slice(-6)}`,
                        status: isOnline ? 'online' : 'offline',
                    })
                }
            })

            setState({
                fleetTotal: filteredCount,
            })
            setState({ fleetOnline: online + running + awake })
            setState({ fleetOffline: offline + shutdown })
            setState({ fleetGps: gpsPoints })
            setState({
                boatModelData: Object.entries(modelCount).map(([m, c]) => ({
                    model: m,
                    count: c,
                })),
            })

            setState({
                boatStatusData: [
                    { boatStatus: 'Online', count: online },
                    { boatStatus: 'Running', count: running },
                    { boatStatus: 'Awake', count: awake },
                    { boatStatus: 'Shutdown', count: shutdown },
                    { boatStatus: 'Offline', count: offline },
                ],
            })

            setState({ loading: false })
        },
        [activeManufacturer]
    )

    useEffect(() => {
        const update = () => {
            const now = new Date()

            setState({
                utcTime: `${now.toUTCString().slice(0, 16)}, ${now
                    .toISOString()
                    .slice(11, 19)} UTC`,
            })
        }
        update()
        const id = setInterval(update, 1000)
        return () => clearInterval(id)
    }, [])

    useEffect(() => {
        console.log(apiSocket, 'apiSock')
        console.log(user, 'user')
        console.log(oupUser, 'oupusers')
        console.log(activeManufacturer, 'activeManufact')
        if (!apiSocket || !oupUser || !activeManufacturer) return

        const handler = (response: any) => {
            let data: any[] = []
            if (Array.isArray(response)) data = response
            else if (response?.data) data = response.data
            else if (response?.result) data = response.result
            else if (response?.boats) data = response.boats

            processFullFleet(data)
        }

        apiSocket.emit('allFleetLiveData', {
            oupUser,
            manufacturerId: activeManufacturer._id,
        })

        apiSocket.once('allFleetLiveDataRes', handler)

        return () => {
            apiSocket.off('allFleetLiveDataRes', handler)
        }
    }, [apiSocket, oupUser, activeManufacturer, processFullFleet])

    useEffect(() => {
        setState({ loading: true })
    }, [activeManufacturer])

    return (
        <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: '#1e1e1e' }}>
            <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>

            <AppBar position="static" color="transparent" elevation={0}>
                <Toolbar sx={{ bgcolor: '#2d2d2d', color: 'white' }}>
                    <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                        <Navigation />
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" fontWeight="bold">
                            OSMOSIS
                        </Typography>
                        <Typography variant="body2" color="grey.500">
                            Fleet Home View
                        </Typography>
                    </Box>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <AccessTime sx={{ color: '#4fc3f7' }} />
                        <Typography variant="body2" fontWeight="bold">
                            {utcTime}
                        </Typography>
                        <Circle
                            sx={{
                                fontSize: 10,
                                color: '#4caf50',
                                animation: 'pulse 2s infinite',
                            }}
                        />
                        <Typography variant="body2" color="#4caf50">
                            Live Tracking
                        </Typography>
                    </Stack>
                </Toolbar>
            </AppBar>

            <Box sx={{ p: 3, py: 3 }}>
                <Paper sx={{ p: 3, mb: 3, bgcolor: '#2d2d2d' }}>
                    <Typography variant="h6" color="white" gutterBottom>
                        Fleet Status
                    </Typography>
                    {loading ? (
                        <Grid container spacing={4}>
                            {[1, 2, 3].map((i) => (
                                <Grid item xs={12} md={4} key={i}>
                                    <Skeleton
                                        variant="text"
                                        width="60%"
                                        height={60}
                                        sx={{ bgcolor: 'grey.800' }}
                                    />
                                    <Skeleton
                                        variant="text"
                                        width="40%"
                                        height={80}
                                        sx={{ bgcolor: 'grey.800' }}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Grid container spacing={4}>
                            <Grid item xs={12} md={4} lg={4}>
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                >
                                    <Typography color="grey.400">
                                        Total boats
                                    </Typography>
                                    <Typography
                                        variant="h4"
                                        fontWeight="bold"
                                        color="white"
                                    >
                                        {fleetTotal}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={4} lg={4}>
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                    >
                                        <Circle
                                            sx={{
                                                fontSize: 12,
                                                color: '#4caf50',
                                            }}
                                        />
                                        <Typography color="grey.400">
                                            Active Boats
                                        </Typography>
                                    </Stack>
                                    <Typography
                                        variant="h4"
                                        fontWeight="bold"
                                        color="#4caf50"
                                    >
                                        {fleetOnline}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={4} lg={4}>
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                    >
                                        <Circle
                                            sx={{
                                                fontSize: 12,
                                                color: 'grey.600',
                                            }}
                                        />
                                        <Typography color="grey.400">
                                            Inactive Boats
                                        </Typography>
                                    </Stack>
                                    <Typography
                                        variant="h4"
                                        fontWeight="bold"
                                        color="grey.600"
                                    >
                                        {fleetOffline}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    )}
                </Paper>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={4} lg={4}>
                        <Paper sx={{ bgcolor: '#2d2d2d', overflow: 'hidden' }}>
                            <Box
                                sx={{
                                    p: 2,
                                    borderBottom: 1,
                                    borderColor: 'grey.800',
                                }}
                            >
                                <Typography variant="h6" color="white">
                                    Map Fleet View
                                </Typography>
                                <Typography variant="caption" color="grey.500">
                                    Green - online, Gray - offline.
                                </Typography>
                            </Box>
                            <Box sx={{ height: 600, bgcolor: '#111' }}>
                                {loading ? (
                                    <Skeleton
                                        variant="rectangular"
                                        width="100%"
                                        height="100%"
                                        sx={{ bgcolor: 'grey.900' }}
                                    />
                                ) : (
                                    <UniversalGoogleMapV2
                                        fleetGps={fleetGps}
                                        style={{
                                            width: '100%',
                                            height: '600px',
                                            backgroundColor: 'inherit',
                                        }}
                                        mapZoom={6}
                                    />
                                )}
                            </Box>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={8} lg={8}>
                        <Paper sx={{ bgcolor: '#2d2d2d', minHeight: 600 }}>
                            <Box
                                sx={{
                                    p: 3,
                                    borderBottom: 1,
                                    borderColor: 'grey.800',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Box>
                                    <Typography variant="h6" color="white">
                                        Osmosis Report
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="grey.400"
                                    >
                                        Welcome to the Osmosis Fleet Report.
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        bgcolor: '#4caf50',
                                        px: 3,
                                        py: 1,
                                        borderRadius: 1,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="white"
                                        fontWeight="bold"
                                    >
                                        OSMOSIS
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ p: 3 }}>
                                {loading ? (
                                    <Grid container spacing={3}>
                                        {[1, 2].map((i) => (
                                            <Grid item xs={12} md={6} key={i}>
                                                <Skeleton
                                                    variant="rectangular"
                                                    height={300}
                                                    sx={{ bgcolor: 'grey.800' }}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                ) : (
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={6} lg={6}>
                                            <BoatModelChart
                                                title="Boats by Boat Model"
                                                data={boatModelData}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6} lg={6}>
                                            <BoastStatusChart
                                                title="Fleet Boat Status"
                                                data={boatStatusData}
                                            />
                                        </Grid>
                                    </Grid>
                                )}
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    )
}

export default FleetHomeView
